<template>
  <div class="p-40 max-w-700">
    <ElForm
      :model="accountDetails"
      :rules="accountDetailsFormRules"
      ref="username-form"
      label-position="top"
      label-width="120px"
      status-icon
      class="flex items-end small:flex-col small:items-start"
      @submit.native.prevent
    >
      <ElFormItem
        label="Username"
        prop="username"
        class="w-full mr-10"
      >
        <ElInput
          ref="username-form-input"
          v-model="accountDetails.username"
          name="test"
          placeholder="Joeven Doe"
          @keyup.enter.native="onUpdateUsernamePress"
        />
      </ElFormItem>
      <ElFormItem>
        <ElButton
          ref="username-form-button"
          type="primary"
          class="h-42"
          @click="onUpdateUsernamePress"
        >
          Update
        </ElButton>
      </ElFormItem>
    </ElForm>
    <ElForm
      :model="accountDetails"
      :rules="accountDetailsFormRules"
      ref="email-form"
      label-position="top"
      label-width="120px"
      status-icon
      class="flex items-end small:flex-col small:items-start small:mt-10"
      @submit.native.prevent
    >
      <ElFormItem
        label="Email"
        prop="email"
        class="w-full mr-10"
      >
        <ElInput
          v-model="accountDetails.email"
          ref="email-form-input"
          placeholder="name@example.com"
          :disabled="!userAuthData.providers.includes('password')"
        />
      </ElFormItem>
      <ElFormItem>
        <ElButton
          type="primary"
          :disabled="!userAuthData.providers.includes('password')"
          class="h-42"
          @click="onUpdateEmailPress"
        >
          Change Email
        </ElButton>
      </ElFormItem>
    </ElForm>

    <ElForm
      v-if="userAuthData.providers.includes('password')"
      :model="accountDetails"
      :rules="accountDetailsFormRules"
      ref="new-password-form"
      label-position="top"
      label-width="120px"
      status-icon
      class="flex items-end small:flex-col small:items-start small:mt-10"
      @submit.native.prevent
    >
      <ElFormItem
        label="New password"
        prop="newPassword"
        class="w-full mr-10"
      >
        <ElInput
          v-model="accountDetails.newPassword"
          ref="new-password-input"
          placeholder="*******"
          type="password"
          @click="onUpdatePasswordPress"
        />
      </ElFormItem>
      <ElFormItem>
        <ElButton
          type="primary"
          class="h-42"
          @click="onUpdatePasswordPress"
        >
          Change Password
        </ElButton>
      </ElFormItem>
    </ElForm>

    <template v-if="!userProvidersIncludesPassword">
      <h2 class="leading-tight mt-40 small:mt-20">Use your work Email to sign-in to Eventor</h2>
      <p class="leading-tight mt-10">You can link your Eventor account with an Email and password.</p>
      <ElButton
        type="primary"
        class="mt-20"
        @click="onLinkAccountWithEmailPassword"
      >
        <FontAwesomeIcon
          :icon="['fas', 'envelope']"
          size="lg"
          class="mr-15"
        />
        Link with Email + Password
      </ElButton>
    </template>

    <h2 class="leading-tight mt-40 small:mt-20">Use your Google/Facebook account to sign-in</h2>
    <p class="leading-tight mt-10">
      You can link your Eventor account with Google/Facebook so that you can sign-in using your Google/Facebook
      accounts.
    </p>
    <div class="mt-20 small:flex small:flex-col">
      <ElButton
        type="primary"
        class="signin-google mr-10 small:mr-0"
        :disabled="userAuthData.providers.length === 1 && userProvidersIncludesGoogle"
        @click="onAuthProviderPress('google.com')"
      >
        <FontAwesomeIcon
          :icon="['fab', 'google']"
          size="lg"
          class="mr-15"
        />
        <template v-if="!userProvidersIncludesGoogle">
          Link with Google
        </template>
        <template v-else-if="userAuthData.providers.length === 1 && userProvidersIncludesGoogle">
          Linked to Google
        </template>
        <template v-else>
          Unlink Google
        </template>
      </ElButton>

      <ElButton
        type="primary"
        class="signin-facebook small:mt-10"
        :disabled="userAuthData.providers.length === 1 && userProvidersIncludesFacebook"
        @click="onAuthProviderPress('facebook.com')"
      >
        <FontAwesomeIcon
          :icon="['fab', 'facebook-f']"
          size="lg"
          class="mr-15"
        />
        <template v-if="!userProvidersIncludesFacebook">
          Link with Facebook
        </template>
        <template v-else-if="userAuthData.providers.length === 1 && userProvidersIncludesFacebook">
          Linked with Google
        </template>
        <template v-else>
          Unlink Facebook
        </template>
      </ElButton>
    </div>
    <p class="font-bold italic text-right mt-40">
      {{ `v. ${appVersion}` }}
    </p>
    <ElDialog
      :visible="dialogIsVisible"
      width="100%"
      top="6vh"
      @close="dialogIsVisible = false"
    >
      <div
        slot="title"
        class="truncate"
      >
        {{ dialogTitle }}
      </div>

      <ElForm
        :model="dialogForm"
        :rules="dialogType === 'link-account-with-email-password' ? dialogFormRules : accountDetailsFormRules"
        ref="dialog-form"
        label-position="top"
        label-width="120px"
        status-icon
        @submit.native.prevent
      >
        <ElFormItem
          v-if="dialogType === 'link-account-with-email-password'"
          label="Email"
          prop="email"
          class="w-full mr-10"
        >
          <ElInput
            v-model="dialogForm.email"
            placeholder="your@example.com"
            type="text"
          />
        </ElFormItem>
        <ElFormItem
          :label="dialogType === 'link-account-with-email-password' ? 'Password' : 'Current password'"
          prop="currentPassword"
          :error="currentPasswordFormItemError"
          class="w-full mr-10"
        >
          <ElInput
            v-model="dialogForm.currentPassword"
            placeholder="*******"
            type="password"
            @keyup.enter.native="onDialogConfirmPress"
          />
        </ElFormItem>
      </ElForm>

      <div
        slot="footer"
        class="dialog-footer text-right"
      >
        <ElButton
          type="text"
          :disabled="dialogButtonsIsDisabled"
          class="mr-15"
          @click="dialogIsVisible = false"
        >
          Cancel
        </ElButton>
        <ElButton
          type="primary"
          :disabled="dialogButtonsIsDisabled"
          @click="onDialogConfirmPress"
        >
          Confirm
        </ElButton>
      </div>
    </ElDialog>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { showErrorNotification, showSuccessNotification } from '@/utils/notification';

export default {
  data() {
    const validateUsername = (rule, value, callback) => {
      if (!value.trim() || value.trim() === '') {
        callback('Please enter a valid username.');
      } else if (this.accountDetails.username.trim() === this.userAuthData.displayName) {
        callback('Please enter a different username.');
      }
      callback();
    };
    const validateNewEmail = (rule, value, callback) => {
      const re = /^.+@.+\..+$/;

      if (!value || value.length === 0) {
        callback();
      } else if (!re.test(value) || value.length >= 256 || value.includes(' ')) {
        callback('Please enter a valid Email');
      } else if (this.accountDetails.email.trim() === this.userAuthData.email) {
        callback('Please enter a different Email address.');
      }
      callback();
    };
    const validateEmail = (rule, value, callback) => {
      const re = /^.+@.+\..+$/;

      if (!value || value.length === 0) {
        callback();
      } else if (!re.test(value) || value.length >= 256 || value.includes(' ')) {
        callback('Please enter a valid Email');
      }
      callback();
    };
    const validateNewPassword = (rule, value, callback) => {
      if (value === '') {
        callback('Please enter a new password.');
      } else if (value.length < 6) {
        callback('Please enter a valid password (more than 5 characters)');
      }
      callback();
    };

    return {
      dialogIsVisible: false,
      dialogTitle: '',
      dialogType: '',
      dialogButtonsIsDisabled: false,
      currentPasswordFormItemError: '',
      accountDetailsFormRules: {
        username: [{
          validator: validateUsername,
          trigger: 'blur',
        }],
        email: [{
          validator: validateNewEmail,
          trigger: 'blur',
        }],
        newPassword: [{
          validator: validateNewPassword,
          trigger: 'change',
        }],
      },
      dialogFormRules: {
        email: [{
          validator: validateEmail,
          trigger: 'blur',
        }],
        currentPassword: [{
          validator: validateNewPassword,
          trigger: 'change',
        }],
      },
      accountDetails: {
        username: this.$store.state.user.auth.authData.displayName,
        email: this.$store.state.user.auth.authData.email,
        newPassword: '',
      },
      dialogForm: {
        email: '',
        currentPassword: '',
      },
      appVersion: process.env.VUE_APP_VERSION,
    };
  },
  created() {
    if (process.env.VUE_APP_MODE === 'development' || process.env.NODE_ENV === 'development') {
      this.$store.dispatch('user/auth/getAuthToken').then((authToken) => {
        console.log(`Bearer ${authToken}`);
      });
    }
  },
  computed: {
    ...mapState('user/auth', {
      userAuthData: 'authData',
    }),
    userProvidersIncludesPassword() {
      return this.userAuthData.providers.includes('password');
    },
    userProvidersIncludesGoogle() {
      return this.userAuthData.providers.includes('google.com');
    },
    userProvidersIncludesFacebook() {
      return this.userAuthData.providers.includes('facebook.com');
    },
  },
  methods: {
    ...mapActions('user/auth', {
      updateProfile: 'updateProfile',
      updateEmail: 'updateEmail',
      updatePassword: 'updatePassword',
      linkWithCredential: 'linkWithCredential',
      reauthenticateWithCredential: 'reauthenticateWithCredential',
      sendEmailVerification: 'sendEmailVerification',
      linkAccountWithProvider: 'linkAccountWithProvider',
      unlinkAccountWithProvider: 'unlinkAccountWithProvider',
    }),
    async onUpdateUsernamePress() {
      await this.$refs['username-form'].validate();

      this.updateProfile(this.accountDetails.username.trim())
        .then(() => {
          this.accountDetails.username = this.accountDetails.username.trim();
          this.$refs['username-form-input'].blur();
          this.$refs['username-form-button'].$el.blur();
          this.$refs['username-form'].clearValidate();

          showSuccessNotification('Your username has been updated successfully.');
        })
        .catch((error) => {
          console.error(error);
          showErrorNotification('Your username couldn\'t be updated, please try again.');
        });
    },
    async onUpdateEmailPress() {
      await this.$refs['email-form'].validate();

      this.dialogIsVisible = true;
      this.dialogType = 'update-email';
      this.dialogTitle = 'Confirm your password change';
      this.dialogForm.currentPassword = '';
      this.dialogButtonsIsDisabled = false;
    },
    async onUpdatePasswordPress() {
      await this.$refs['new-password-form'].validate();

      this.dialogIsVisible = true;
      this.dialogType = 'update-password';
      this.dialogTitle = 'Confirm your password change';
      this.dialogForm.currentPassword = '';
      this.dialogButtonsIsDisabled = false;
    },
    onLinkAccountWithEmailPassword() {
      this.dialogIsVisible = true;
      this.dialogType = 'link-account-with-email-password';
      this.dialogTitle = 'Enter an email and password';
      this.dialogForm.email = '';
      this.dialogForm.currentPassword = '';
    },
    async onDialogConfirmPress() {
      this.dialogButtonsIsDisabled = true;

      if (this.dialogType === 'update-password') {
        this.onConfirmCurrentPassword()
          .then(() => {
            this.updatePassword(this.accountDetails.newPassword)
              .then(() => {
                this.dialogIsVisible = false;
                this.currentPasswordFormItemError = '';
                this.dialogForm.currentPassword = '';
                this.accountDetails.newPassword = '';

                this.$nextTick(() => {
                  this.$refs['new-password-input'].blur();
                  this.$refs['new-password-form'].clearValidate();
                });

                showSuccessNotification('Your password was changed successfully.');
              })
              .catch((error) => {
                console.error(error);
                this.dialogButtonsIsDisabled = false;
                showErrorNotification(`Your password couldn't be changed (${error.message}). Please try again.`, 7500);
              });
          })
          .catch((error) => {
            console.error(error);
            this.dialogButtonsIsDisabled = false;
            this.currentPasswordFormItemError = 'The password is invalid. Please try again.';
          });
      } else if (this.dialogType === 'update-email') {
        this.onConfirmCurrentPassword()
          .then(() => {
            this.updateEmail(this.accountDetails.email)
              .then(() => {
                this.dialogIsVisible = false;
                this.currentPasswordFormItemError = '';
                this.dialogForm.currentPassword = '';
                this.accountDetails.newPassword = '';

                this.$nextTick(() => {
                  this.$refs['email-form-input'].blur();
                  this.$refs['email-form'].clearValidate();
                });

                showSuccessNotification('Your Email address was changed successfully.');

                // Sending Email verification link
                this.sendEmailVerification()
                  .then(() => {
                    showSuccessNotification('We sent you a link to verify your Email. If you do not find it in Inbox, please also check your Junk/Spam folder.', 7000);
                  })
                  .catch((error) => {
                    showErrorNotification(error.message);
                  });
              })
              .catch((error) => {
                console.error(error);
                this.dialogButtonsIsDisabled = false;
                showErrorNotification(`Your Email address couldn't be changed (${error.message}).`, 7500);
              });
          })
          .catch((error) => {
            console.error(error);
            this.dialogButtonsIsDisabled = false;
            this.currentPasswordFormItemError = 'The password is invalid. Please try again.';
          });
      } else if (this.dialogType === 'link-account-with-email-password') {
        try {
          await this.linkWithCredential({
            email: this.dialogForm.email,
            password: this.dialogForm.currentPassword,
          });

          this.accountDetails.email = this.dialogForm.email;
          this.dialogIsVisible = false;
          this.dialogForm.email = '';
          this.dialogForm.currentPassword = '';

          showSuccessNotification('Your account was linked with your Email and password successfully. You can now sign-in Eventor using this Email and password.', 5000);

          // Sending Email verification link
          this.sendEmailVerification()
            .then(() => {
              showSuccessNotification('We sent you a link to verify your Email. If you do not find it in Inbox, please also check your Junk/Spam folder.', 7500);
            })
            .catch((error) => {
              showErrorNotification(error.message);
            });
        } catch (error) {
          console.error(error);
          this.dialogButtonsIsDisabled = false;
          showErrorNotification(`Your account couldn't be linked (${error.message}).`, 7500);
        }
      }
    },
    async onConfirmCurrentPassword() {
      await this.$refs['dialog-form'].validate();

      return this.reauthenticateWithCredential({
        email: this.userAuthData.email,
        password: this.dialogForm.currentPassword,
      });
    },
    onAuthProviderPress(providerId) {
      let providerName = '';

      if (providerId === 'google.com') providerName = 'Google';
      else if (providerId === 'facebook.com') providerName = 'Facebook';

      if ((!this.userProvidersIncludesGoogle && providerId === 'google.com')
        || (!this.userProvidersIncludesFacebook && providerId === 'facebook.com')) {
        this.linkAccountWithProvider(providerId)
          .then(() => {
            showSuccessNotification(`Your account was linked successfully with your ${providerName} account`);
          })
          .catch((error) => {
            console.error(error);
            showErrorNotification(`Your ${providerName} account couldn't be linked. ${error.message}`, 5500);
          });
      } else {
        this.unlinkAccountWithProvider(providerId)
          .then(() => {
            showSuccessNotification(`Your account was unlinked successfully from your ${providerName} account`);
          })
          .catch((error) => {
            console.error(error);
            showErrorNotification(`Your ${providerName} account couldn't be unlinked. ${error.message}`, 5500);
          });
      }
    }
    ,
  },
};
</script>

<style scoped>
.signin-google {
  @apply bg-brand-google border-brand-google;

  &:hover, &:focus {
    @apply bg-brand-google border-brand-google opacity-75;
  }

  &.is-disabled {
    @apply opacity-50;
  }
}

.signin-facebook {
  @apply bg-brand-facebook border-brand-facebook ml-0;

  &:hover, &:focus {
    @apply bg-brand-facebook border-brand-facebook opacity-75;
  }

  &.is-disabled {
    @apply opacity-50;
  }
}
</style>
